// Ingest API client.
//
// Decision history:
//   Session 17: legacy POST {pdf, courseTitle?, hint?} → narrow result type.
//   Session 19E (§4): use full IngestPackage type from shared (so optional
//     fields like proofDrills / glossary surface in TS). (postIngestForDraft
//     removed session 20 — zero callers, superseded by SSE/pollWorkflow paths.)
//   Session 11 (19BI pipeline alignment): added v4 endpoint functions —
//     lockOutline, postRegenerate, confirmRegenerate, getRegenHistory,
//     rollbackRegen, generateMock, cancelIngest, runAudit.

import type {
  IngestPackage,
  CurriculumOutline,
  RegenAction,
  MockPaperSpecV2,
  Discipline,
  ContentLanguage,
  LintReport,
} from '@examforge/shared/schemas';
import { IngestPackageSchema, LintReportSchema } from '@examforge/shared/schemas';
import {
  ExtractOutlineResponseSchema,
  LockOutlineResponseSchema,
  RegenerateResponseSchema,
  RegenerateConfirmResponseSchema,
  RegenHistoryResponseSchema,
  RegenRollbackResponseSchema,
  GenerateMockResponseSchema,
  CancelResponseSchema,
  AuditResponseSchema,
  IngestStatusResponseSchema,
  QuickRegenResponseSchema,
} from '@examforge/shared/api';
import type { QuickRegenRequest, QuickRegenResponse } from '@examforge/shared/api';
import { HealthResponseSchema, RegenerateSyncResponseSchema, RegenerateStageResponseSchema } from '@examforge/shared/schemas';
import type { IngestStatusResponse } from '@examforge/shared/api';
import { IngestContractError } from '@examforge/shared/errors';
import { z } from 'zod';
// 19BN Phase 0: real anonId for body fields (header injection is centralized
// in postV4 via ingestHeaders; body fields are kept for zod but now carry the
// same real value so header/body stay consistent).
import { getOrCreateAnonId } from '../auth/anon-id.js';
// 19BL Session 3a: postV4/getV4 + IngestApiError/IngestErrorResponse + INGEST_URL
// now live in lib/api-client.ts (single home for the v4 HTTP helpers so later
// sessions can add cross-cutting concerns once). Re-exported below to preserve
// every existing import path + the `instanceof IngestApiError` checks across
// friendly-error / use-regen-action / repair-dialog and the test suite.
import {
  postV4,
  getV4,
  INGEST_URL,
  ingestHeaders,
  IngestApiError,
  type IngestErrorResponse,
} from '../lib/api-client.js';

// Re-export so callers importing from api/ingest.js are unaffected.
export { IngestApiError, type IngestErrorResponse };

/** Shape returned by POST /ingest on success. */
export interface IngestResult {
  ok: true;
  package: IngestPackage;
  meta: {
    inputTokens: number;
    outputTokens: number;
    durationMs: number;
    providerName: string;
    /**
     * Session 19BO+ (DEBT-19BO+-LINTREPORT-IDB-PERSIST · 批次 C4 · 2026-06-29):
     * post-completion lint verdict persisted into IDB so it survives reload.
     * Previously lintReport lived only in React state (use-course-ingest.ts),
     * so a refresh lost the verdict and the done-view LintSummary could no
     * longer show the quality verdict. (NOTE: an older draft blamed
     * deriveOverallState; that 八态机 is dead code —
     * DEBT-19BO+-OVERALL-STATE-DEAD-CODE — so the real impact was on
     * LintSummary.) Optional: legacy records
     * and paste-json results without lint have none.
     */
    lintReport?: LintReport;
  };
}

/**
 * IngestResultSchema — runtime validation gate for putResult (DEBT-19BO+-
 * PUTRESULT-SCHEMA-GATE · 批次 C3 · 2026-06-29).
 *
 * Why this exists: putResult previously did a bare `s.put(record)` with no
 * validation, so malformed records (stale schema residue, partial writes,
 * botched manual paste-json) silently entered IDB and only blew up the UI on
 * next read — the error scene was lost. Now putResult safeParses first;
 * on failure it console.errors the zod issues but STILL WRITES (refusing
 * would discard user content, which is worse — backlog-confirmed decision).
 *
 * Shape mirrors `IngestResult` above exactly. Uses IngestPackageSchema (which
 * is itself .passthrough()) so forward-compatible extra package fields from
 * future worker versions don't trip the gate. meta is .passthrough() too.
 */
export const IngestResultMetaSchema = z.object({
  inputTokens: z.number(),
  outputTokens: z.number(),
  durationMs: z.number(),
  providerName: z.string(),
  lintReport: LintReportSchema.optional(),
}).passthrough();

export const IngestResultSchema = z.object({
  ok: z.literal(true),
  package: IngestPackageSchema,
  meta: IngestResultMetaSchema,
}).passthrough();

/**
 * Encode a File to base64 (no data: prefix).
 * Uses FileReader for browser compatibility; works for any reasonable PDF size.
 */

/**
 * /health response shape (Session 19AC + 19AQ).
 * Worker returns legacyProvider/legacyModel + router.routingTable; we expose
 * a frontend-friendly summary.
 */
export interface HealthResponse {
  ok: boolean;
  legacyProvider?: string;
  legacyModel?: string;
  router?: {
    enabledStages?: readonly string[];
    routingTable?: Record<string, string>;
  } | { error: string };
  keysConfigured?: { glm?: boolean; deepseek?: boolean; anthropic?: boolean };
}

export async function checkHealth(): Promise<{ ok: boolean; provider: string; model: string }> {
  const res = await fetch(`${INGEST_URL}/health`);
  if (!res.ok) throw new Error(`health check failed: ${res.status}`);
  const rawJson: unknown = await res.json();
  // Session 39: runtime Zod validation replaces bare `as HealthResponse` cast.
  const parsed = HealthResponseSchema.safeParse(rawJson);
  if (!parsed.success) {
    // Non-fatal: health check is advisory — return degraded info rather than crash.
    // eslint-disable-next-line no-console
    console.warn('[health] response schema violation, using defaults:', parsed.error.issues);
    return { ok: false, provider: 'unknown', model: 'unknown' };
  }
  const raw = parsed.data;
  // Session 19AQ · F1 fix: badge previously read `data.provider` + `data.model`
  // (top-level fields that don't exist) → "undefined · undefined" in UI.
  // Resolve now: prefer router's primary stage model (extract_cases is the
  // first generative LLM call so its routed model is the "primary" provider
  // for this deployment), fall back to legacy fields, then 'unknown'.
  const router = raw.router && 'routingTable' in raw.router ? raw.router : null;
  const primaryModel =
    router?.routingTable?.['extract_cases']
    ?? router?.routingTable?.['flashcards']
    ?? raw.legacyModel
    ?? 'unknown';
  // Provider tag derived from model name prefix.
  const provider = primaryModel.startsWith('deepseek') ? 'DeepSeek'
    : primaryModel.startsWith('glm') ? 'GLM'
    : primaryModel.startsWith('claude') ? 'Anthropic'
    : raw.legacyProvider ?? 'unknown';
  return { ok: raw.ok, provider, model: primaryModel };
}

// ── Partial regeneration (19BI · Refine Center) ────────────────────────────────

// ── Extract-outline (DEBT-19BI-EXTRACT-CONFIRM-STEP) ───────────────────────

/**
 * DEBT-19BI-EXTRACT-CONFIRM-STEP: call POST /ingest/extract-outline to get
 * a CurriculumOutline from source text via a single LLM call. Synchronous
 * (~3-8s). User reviews the outline in frontend before confirming and
 * starting the full ingest pipeline.
 */
export async function extractOutline(params: {
  courseId: string;
  courseTitle: string;
  sourceText: string;
  hint?: string;
  /** 19BO+: thread these into the extract LLM call so the outline is generated
   *  with the right discipline tone + content language from the first call. */
  discipline?: Discipline;
  customDiscipline?: string;
  contentLanguage?: ContentLanguage;
}): Promise<CurriculumOutline> {
  const res = await fetch(`${INGEST_URL}/ingest/extract-outline`, {
    method: 'POST',
    headers: ingestHeaders(),
    body: JSON.stringify(params),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new IngestApiError(res.status, json as IngestErrorResponse);
  }
  const parsed = ExtractOutlineResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new IngestContractError(res.status, parsed.error, json);
  }
  return parsed.data.outline;
}

// ── Partial regeneration (19BI · Refine Center) ────────────────────────────────

export interface RegenerateStageParams {
  /** Source slot payloads — must include parsed markdown for backend SlotPayloadSchema. */
  slots: Array<{
    id: string; kind: string; format: string; filename: string;
    markdown: string; byteLength: number;
    label?: string; year?: number; priority?: number;
    customKindNote?: string; parseWarnings?: string[];
  }>;
  /** Course ID — required by backend RegenerateStageRequestSchema. */
  courseId: string;
  /** Course-level config. */
  courseTitle: string;
  enabledComponents: string[];
  discipline: string;
  pedagogyTypes: string[];
  examFormat: Record<string, unknown>;
  /** Which stage to regenerate. */
  stage: string;
  /** Chapter id (omit for course-level stages). */
  chapterId?: string;
  /** 'replace' overwrites, 'add' appends. */
  mode: 'replace' | 'add';
  /** mode=add: how many new items. */
  addCount?: number;
  /** mode=add: existing item ids to exclude. */
  excludeIds?: string[];
  /** Free-form user hint. */
  customHint?: string;
}

export interface RegenerateStageResponse {
  runId: string;
  instanceId: string;
  statusUrl: string;
}

/** Sync fallback response when no Cloudflare Workflow binding (local dev). */
export interface RegenerateSyncResponse {
  partialRegen: true;
  stage: string;
  chapterId?: string;
  mode: string;
  items: unknown[];
  stageError?: string;
  _syncFallback: true;
  _durationMs: number;
}

export type RegenerateAnyResponse = RegenerateStageResponse | RegenerateSyncResponse;

// IngestStatusResponse is now imported from @examforge/shared/api — re-export for
// backward compat with any consumers that import from this module.
export type { IngestStatusResponse } from '@examforge/shared/api';

/** v4 regen workflow result stored in D1 result_json when v4 regen completes.
 *  Re-exported from shared/api — single source of truth. */
export type { V4RegenResult as RegenWorkflowResult } from '@examforge/shared/api';

/**
 * @deprecated Migrate callers to POST /ingest/regenerate (v4).
 * Legacy sync fallback — to be removed once all frontend regen flows
 * use the v4 workflow path.
 */
export async function postRegenerateStage(params: RegenerateStageParams): Promise<RegenerateAnyResponse> {
  const res = await fetch(`${INGEST_URL}/ingest/regenerate-stage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const json: unknown = await res.json().catch(() => ({}));
  // Sync fallback response includes _syncFallback even on error
  if (typeof json === 'object' && json !== null && '_syncFallback' in json) {
    // Session 39: runtime Zod validation replaces bare `as` cast.
    const parsed = RegenerateSyncResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new IngestContractError(res.status, parsed.error, json);
    }
    return parsed.data;
  }
  if (!res.ok || (typeof json === 'object' && json !== null && 'error' in json)) {
    throw new IngestApiError(res.status, json as IngestErrorResponse);
  }
  // Session 39: runtime Zod validation replaces bare `as` cast.
  const parsed = RegenerateStageResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new IngestContractError(res.status, parsed.error, json);
  }
  return parsed.data;
}

export async function pollIngestStatus(runId: string): Promise<IngestStatusResponse> {
  const res = await fetch(`${INGEST_URL}/ingest/status?runId=${encodeURIComponent(runId)}`);
  if (!res.ok) throw new IngestApiError(res.status, await res.json().catch(() => ({ error: 'poll failed' })));
  const raw = await res.json();
  const parsed = IngestStatusResponseSchema.safeParse(raw);
  if (!parsed.success) throw new IngestContractError(res.status, parsed.error, raw);
  // Cast to hand-written IngestStatusResponse — z.infer degrades because
  // StatusResultSchema contains IngestPackageSchema (ZodObject<ZodRawShape>
  // annotation). Runtime shape is validated by the schema above.
  return parsed.data as IngestStatusResponse;
}

// ── v4 endpoints (19BI pipeline alignment) ────────────────────────────────────
//
// postV4 / getV4 now live in lib/api-client.ts (19BL Session 3a). The 9
// endpoint functions below import them from there. Nothing here changed
// behavior — pure mechanical extraction; the friendly-error + api-endpoints
// characterization suites pin the contract.

// ── Lock-outline ──────────────────────────────────────────────────────────────

export async function lockOutline(params: {
  courseId: string;
  outline: CurriculumOutline;
}) {
  return postV4('/ingest/lock-outline', params, LockOutlineResponseSchema);
}

// ── v4 Regenerate (Phase 1 — 2-phase flow) ────────────────────────────────────
//
// 19BN Phase 0: anonId is sent via X-Anon-Id header (postV4 injects it).
// The body field is kept (backend zod requires it) but its value is the real
// anonId too — header and body stay consistent for debuggability. The old
// 'web-client' fallback is gone.

export async function postRegenerate(params: {
  courseId: string;
  anonId?: string;
  action: RegenAction;
}) {
  return postV4('/ingest/regenerate', {
    courseId: params.courseId,
    anonId: params.anonId ?? getOrCreateAnonId(),
    action: params.action,
  }, RegenerateResponseSchema);
}

// ── Quick Regen (sync, <30s) — 19BJ Session 2 ──────────────────────────────

/** Quick Regen calls runRegenCore() directly in the Worker handler rather than
 *  spawning a Cloudflare Workflow. Suitable for small-scope operations
 *  (≤5 items, ≤3 concepts, 1 case, or delete). Returns the full result
 *  synchronously. */
export async function postQuickRegen(params: QuickRegenRequest): Promise<QuickRegenResponse> {
  return postV4('/ingest/quick-regen', params, QuickRegenResponseSchema);
}

// ── Regenerate confirm (Phase 2) ──────────────────────────────────────────────

export async function confirmRegenerate(params: {
  regenId: string;
  decision: 'apply' | 'reject';
  editedItems?: unknown[];
}) {
  return postV4('/ingest/regenerate-confirm', params, RegenerateConfirmResponseSchema);
}

// ── Regen history ─────────────────────────────────────────────────────────────

export async function getRegenHistory(courseId: string, limit?: number) {
  const query = limit
    ? `/ingest/regen-history?courseId=${encodeURIComponent(courseId)}&limit=${limit}`
    : `/ingest/regen-history?courseId=${encodeURIComponent(courseId)}`;
  return getV4(query, RegenHistoryResponseSchema);
}

// ── Regen rollback ────────────────────────────────────────────────────────────

export async function rollbackRegen(params: {
  courseId: string;
  regenId: string;
  anonId?: string;
}) {
  return postV4('/ingest/regen-rollback', {
    courseId: params.courseId,
    anonId: params.anonId ?? getOrCreateAnonId(),
    regenId: params.regenId,
  }, RegenRollbackResponseSchema);
}

// ── Generate mock (usage-time) ────────────────────────────────────────────────

export async function generateMock(params: {
  courseId: string;
  spec: MockPaperSpecV2;
  anonId?: string;
}) {
  return postV4('/ingest/generate-mock', {
    courseId: params.courseId,
    anonId: params.anonId ?? getOrCreateAnonId(),
    spec: params.spec,
  }, GenerateMockResponseSchema);
}

// ── Cancel ────────────────────────────────────────────────────────────────────

export async function cancelIngest(runId: string) {
  return postV4('/ingest/cancel', { runId }, CancelResponseSchema);
}

// ── Audit ─────────────────────────────────────────────────────────────────────

export async function runAudit(params: {
  courseId: string;
  pkg: unknown;
  outline: CurriculumOutline;
}) {
  return postV4('/ingest/audit', params, AuditResponseSchema);
}
